import { type ReactElement, useCallback, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Fade,
  Typography,
} from '@mui/material';
import type { AccordionSlots } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import type { PpWC } from '@noah-labs/fe-shared-ui-shared';
import { ChevronDown } from 'lucide-react';

const StyledAccordion = styled(Accordion)<{ expanded: boolean }>(({ expanded, theme }) => ({
  margin: 0,
  ':before': {
    display: 'none',
  },
  '& .MuiAccordion-region': {
    height: expanded ? 'auto' : 0,
  },
  '& .MuiAccordionDetails-root': {
    display: expanded ? 'block' : 'none',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: expanded ? theme.palette.websiteBrand.primary : 'inherit',
  },
  '& .MuiAccordionSummary-root, .MuiAccordionDetails-root': {
    padding: 0,
  },
}));

type PpMobileMenuAccordion = PpWC & {
  summary: string | null | undefined;
};

export function MobileMenuAccordion({ children, summary }: PpMobileMenuAccordion): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const handleExpansion = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, [setExpanded]);

  return (
    <StyledAccordion
      disableGutters
      square
      elevation={0}
      expanded={expanded}
      slotProps={{ heading: { component: 'span' }, transition: { timeout: 400 } }}
      slots={{ transition: Fade as AccordionSlots['transition'] }}
      onChange={handleExpansion}
    >
      <AccordionSummary expandIcon={<ChevronDown size={24} />}>
        <Typography
          color={expanded ? 'text.secondary' : 'text.primary'}
          sx={{
            ':hover': {
              color: 'text.secondary',
            },
          }}
          variant="subHeadingM"
        >
          {summary}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Divider sx={{ borderColor: theme.palette.websiteBrand.primary }} />
        {children}
      </AccordionDetails>
    </StyledAccordion>
  );
}
